import { Table, Badge, Group, ActionIcon, Tooltip, Text, Button, Box, LoadingOverlay } from '@mantine/core';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers, deleteUser } from '../../../redux/reducers/userSlice';
import { IconEdit, IconTrash, IconPlus } from '@tabler/icons-react';
import { selectUsersSelector } from '../../../redux/selectors/userSelector';
import { selectUserLoading } from '../../../redux/selectors/userSelector';
import { User } from '../../../models/user.model';
import { selectIsAdminOrOwner } from '../../../redux/selectors/authSelector';
import AddEditUser from './add_edit_user/add_edit_user';
import ConfirmationModal from '../admin-common/confirmation/confirmation.modal';
import colors from '../../../utils/styles';

function UsersList() {
  const dispatch = useDispatch();
  const users = useSelector(selectUsersSelector);
  const loading = useSelector(selectUserLoading);
  const isAdminOrOwner = useSelector(selectIsAdminOrOwner);

  const [modalOpened, setModalOpened] = useState(false);
  const [activeUser, setActiveUser] = useState<User | null>(null);
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);

  useEffect(() => {
    dispatch(getAllUsers());
  }, [dispatch]);

  const handleOpenModal = (user: User | null = null) => {
    setActiveUser(user);
    setModalOpened(true);
  };

  const handleCloseModal = () => {
    setActiveUser(null);
    setModalOpened(false);
  };

  const handleDeleteUser = (user: User) => {
    setConfirmationModalOpened(true);
    setActiveUser(user);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'blue';
      case 'user':
        return 'green';
      case 'owner':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const rows = useMemo(() => users?.map((user: User) => (
    <Table.Tr key={user._id}>
      <Table.Td>{user.first_name}</Table.Td>
      <Table.Td>
        <Text lineClamp={2}>
          {user.last_name}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text lineClamp={2}>
          {user.email}
        </Text>
      </Table.Td>
      <Table.Td>
        {user.role ? (
          <Badge color={getRoleColor(user.role)}>
            {user.role}
          </Badge>
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>
        {user.user_updated_at ? (
          new Date(user.user_updated_at).toLocaleDateString()
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Tooltip label="Edit User">
            <ActionIcon
              size="lg"
              variant="outline"
              color="blue"
              onClick={() => handleOpenModal(user)}
              disabled={!isAdminOrOwner}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete User">
            <ActionIcon
              size="lg"
              variant="outline"
              color="red"
              disabled={!isAdminOrOwner}
              onClick={() => handleDeleteUser(user)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  )), [users, isAdminOrOwner]);

  return (
    <Box>
      <Group justify="flex-end" mb="md">
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => handleOpenModal()}
          disabled={!isAdminOrOwner}
        >
          Add User
        </Button>
      </Group>

      <Box style={{ position: 'relative' }}>
        <LoadingOverlay
          visible={loading}
          overlayProps={{ radius: "md", blur: 0.5 }}
          loaderProps={{ color: colors.appDarkBlue, type: 'bars' }}
        />
        <Table stickyHeader stickyHeaderOffset={60}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>First Name</Table.Th>
              <Table.Th>Last Name</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Last Updated</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Box>
      <AddEditUser
        opened={modalOpened}
        onClose={handleCloseModal}
        activeUser={activeUser}
      />
      <ConfirmationModal
        text="Are you sure you want to delete this user?"
        opened={confirmationModalOpened}
        onClose={() => {
          setConfirmationModalOpened(false);
          setActiveUser(null);
        }}
        onConfirm={() => {
          dispatch(deleteUser(activeUser?._id as any));
          setConfirmationModalOpened(false);
          setActiveUser(null);
        }}
      />
    </Box>
  );
}

export default UsersList;